import { ApplicationContextEnum } from '../../services/applicationService/enums';
import { isNative as isNativeJWeb, JWebCoreFacade } from '../../services/JWeb';
import { Stack } from '../../core/types/stratus';
import * as T from './types';
import { ManifestType } from '@clientos/types/dist';
import { AnalyticsServiceInputType } from '../../services/AnalyticsService/types';
import AuthenticationProviderEnum from '../../config/authenticationProviderEnum';
import { ManifestEnhancerFactory } from './manifestEnhancers';
import { EnvironmentVariablesInputType } from '../../services/environmentVariablesService/types';

/**
 * Enhanced adapter that converts manifest configuration to Commons initialization parameters
 * Implements separation of concerns and better error handling
 */
export class ManifestAdapter {
  /**
   * Converts manifest to interfaces with validation
   */
  static async ProcessToConvert(
    manifestInput: ManifestType
  ): Promise<T.CommonsInitializeType> {
    //TODO remove this workaround
    (window as any).Shell = (window as any).Shell || {
      manifest: manifestInput
    };
    /**
     * Checking Environment
     */
    const isNative = await isNativeJWeb();

    /**
     * Enhancements on the current manifest
     */
    const manifest = await ManifestEnhancerFactory.enhanceManifest(
      manifestInput,
      isNative
    );

    /**
     * Convert manifest to interfaces object input data
     */
    const commonsInputData = await this.convert(manifest, isNative);

    return commonsInputData;
  }

  /**
   * Converts manifest to Commons configuration
   */
  static async convert(
    manifest: ManifestType,
    isNative: boolean
  ): Promise<T.CommonsInitializeType> {
    // Extract basic portal configuration
    const portalConfig = this.extractPortalConfig(manifest);
    const applicationContext = this.determineApplicationContext(
      manifest,
      isNative
    );
    const jWebPlatform = await JWebCoreFacade.getPlatformCached();
    return {
      ...portalConfig,
      applicationContext,
      isNative,
      jWebPlatform,
      platform: (manifest?.portal?.platform ||
        T.PlatformEnum.auto) as T.PlatformEnum,
      defaultLayoutKey: manifest?.navigation?.defaultLayoutKey,
      // Service configurations
      analytics: this.extractAnalyticsConfig(manifest, isNative),
      logger: this.extractLoggerConfigConfig(manifest, isNative),
      login: this.extractLoginConfig(manifest),
      navigation: this.extractNavigationConfig(manifest),
      shellFlags: this.extractShellFlagsConfig(manifest),
      authProviders: this.extractAuthProvidersConfig(manifest),
      abTesting: this.extractABTestingConfig(manifest),
      ast: this.extractAstConfig(manifest),
      backgroundTasks: this.extractBackgroundTasksConfig(manifest),
      consent: this.extractConsentConfig(manifest, isNative),
      featureFlags: this.extractFeatureFlagsConfig(manifest),
      grants: this.extractGrantsConfig(manifest),
      grantsHistory: this.extractGrantsHistoryConfig(manifest),
      graphql: this.extractGraphQLConfig(manifest),
      idleLogout: this.extractIdleLogoutConfig(manifest),
      layouts: this.extractLayoutsConfig(manifest),
      localization: this.extractLocalizationConfig(manifest),
      monitoring: this.extractMonitoringConfig(manifest),
      onboarding: this.extractOnboardingConfig(manifest),
      routes: this.extractRoutesConfig(manifest),
      serviceWorker: this.extractServiceWorkerConfig(manifest),
      stack: this.extractStackConfig(manifest),
      theme: this.extractThemeConfig(manifest),
      tenantHandlerProps: this.extractTenantHandlerConfig(manifest, isNative),
      urlService: this.extractURLServiceConfig(manifest),
      userActivity: this.extractUserActivityConfig(manifest),
      userOnboarding: this.extractUserOnboardingConfig(manifest),
      event: this.extractEventConfig(manifest),
      mock: this.extractMockConfig(manifest),
      accessControl: this.extractAccessControlConfig(manifest),
      adapter: this.extractAdapterConfig(manifest),
      fallbackConfiguration: this.extractFallbackConfig(manifest),
      entitlements: this.extractEntitlementsConfig(manifest),
      criterions: this.extractCriterionsConfig(manifest),
      breadcrumb: this.extractBreadcrumbConfig(manifest),
      optimizely: this.extractOptimizelyConfig(manifest),
      environmentVariables: this.extractEnvironmentVariablesConfig(manifest),
      overrides: this.extractOverridesConfig(manifest)
    };
  }

  private static extractPortalConfig(manifest: ManifestType) {
    return {
      appName: manifest?.portal?.appName,
      basePath: manifest?.portal?.basePath,
      clientId: manifest?.portal?.clientId,
      stack: this.extractStackConfig(manifest)
    };
  }

  private static extractNavigationConfig(
    manifest: any
  ): T.NavigationServiceInputType {
    const navigationConfig = manifest?.services?.navigation || {};
    const localizationConfig = manifest?.services?.localization || {};

    return {
      useLegacyNavigation: manifest?.navigation?.useLegacyNavigation,
      removeLocaleUrl: localizationConfig?.removeLocaleUrl,
      useVirtualHistory: navigationConfig?.useVirtualHistory,
      blockNavigation: {
        pathList: this.extractBlockNavigationPaths(navigationConfig)
      }
    };
  }

  private static extractBlockNavigationPaths(navigationConfig: any): any[] {
    const pathList = navigationConfig?.blockNavigation?.pathList;

    if (!Array.isArray(pathList)) {
      return [];
    }

    return pathList;
  }

  private static extractLoggerConfigConfig(
    manifest: any,
    isNative: boolean
  ): T.LoggerInputType {
    const loggerConfig = manifest?.services?.logger || {};

    return {
      ...loggerConfig,
      isNative,
      allowedTypes: {
        debug: false,
        error: true,
        log: false,
        warn: true,
        ...loggerConfig?.allowedTypes
      },
      enableDecorators: loggerConfig?.enableDecorators
    };
  }

  private static extractAuthProvider(manifest: any) {
    const authenticationProvider = manifest?.services?.login
      ?.authenticationProvider
      ? AuthenticationProviderEnum[
          manifest?.services?.login?.authenticationProvider
        ]
      : AuthenticationProviderEnum.authz;
    return authenticationProvider;
  }

  private static extractLoginConfig(manifest: any): T.LoginInputType {
    const loginConfig = manifest?.services?.login || {};

    const authProvider = this.extractAuthProvider(manifest);

    return {
      enabled: Boolean(loginConfig.enable),
      assetReference: loginConfig.assetReference,
      authenticationProvider: authProvider,
      emailGathering: loginConfig.emailGathering || false,
      properties: loginConfig.properties,
      postLoginRedirect: loginConfig.postLoginRedirect,
      preLoginValidation: loginConfig.preLoginValidation,
      minutesBeforeExpiredTokenToRefresh:
        loginConfig.minutesBeforeExpiredTokenToRefresh,
      overrideClientOSAdditionalAuthorizationParams:
        loginConfig.overrideClientOSAdditionalAuthorizationParams,
      syncAuthenticationDataOnJWebValueStore:
        loginConfig.syncAuthenticationDataOnJWebValueStore
    };
  }

  private static extractAnalyticsConfig(
    manifest: any,
    isNative: boolean
  ): AnalyticsServiceInputType {
    return {
      isNative, // At this point, I'm faking this value (it wont be used) but should be fixed.
      userActivity: manifest?.services?.userActivity,
      publishErrors: !!manifest?.services?.analytics?.publishErrors,
      enabled: !!manifest?.services?.analytics?.enable,
      batchEnabled: manifest?.services?.analytics?.batchEnabled,
      batchConfiguration: manifest?.services?.analytics?.batchConfiguration,
      queueConfiguration: manifest?.services?.analytics?.queueConfiguration,
      telemetryAPIkey: manifest?.services?.analytics?.telemetryAPIkey,
      customAnalyticsSubsystem:
        manifest?.services?.analytics?.customAnalyticsSubsystem,
      valveControllerAPIkey:
        manifest?.services?.analytics?.valveControllerAPIkey,
      stack: manifest?.portal?.stack // stack as String
    };
  }

  private static extractStackConfig(manifest: any): Stack {
    let stackName = manifest?.portal?.stack;
    if (!stackName) {
      console.warn('Portal stack is required in manifest');
      stackName = 'dev';
    }

    const stack = Stack[stackName];
    if (stack === undefined) {
      throw new Error(
        `Invalid stack: ${stackName}. Available stacks: ${Object.keys(Stack)
          .filter((key) => isNaN(Number(key)))
          .join(', ')}`
      );
    }
    // That is wrong, but it's already used in the codebase
    // @ts-ignore
    return stack;
  }

  private static determineApplicationContext(
    manifest: ManifestType,
    isNative: boolean
  ): ApplicationContextEnum | undefined {
    if (manifest?.overrides?.enable) {
      return ApplicationContextEnum.ADAPTER;
    }

    if (manifest?.portal?.platform === T.PlatformEnum.nativeOnly) {
      return ApplicationContextEnum.NATIVE_APP;
    }

    if (manifest?.portal?.clientId) {
      return isNative
        ? ApplicationContextEnum.NATIVE_PORTAL
        : ApplicationContextEnum.WEB_PORTAL;
    }

    return undefined;
  }

  private static extractShellFlagsConfig(manifest: any): T.ShellFlagsInputType {
    return {
      oaLooseLoad: manifest?.services?.webPortalsFlags?.oaLooseLoad ?? true,
      nativeRefreshListener:
        manifest?.services?.webPortalsFlags?.nativeRefreshListener ?? true,
      nativeRefreshServicesOnAuthChange:
        manifest?.services?.webPortalsFlags
          ?.nativeRefreshServicesOnAuthChange ?? false,
      webUIRefreshOnAuthChange:
        manifest?.services?.webPortalsFlags?.webUIRefreshOnAuthChange ?? false,
      tenantSelectorSideMenuSkeleton:
        manifest?.services?.webPortalsFlags?.tenantSelectorSideMenuSkeleton ??
        true,
      enableTokenRefreshOnInit:
        manifest?.services?.webPortalsFlags?.enableTokenRefreshOnInit ?? false,
      // TODO maybe we can remove this Shell FF when all OneCloud's portals already have the scopes for using the v3 of the ocrel_registry api, because the v2 will be deprecated by the OneCloud cloud team
      enableLegacyOCRelRegistryApi:
        manifest?.services?.webPortalsFlags?.enableLegacyOCRelRegistryApi ??
        false
    };
  }

  private static extractMockConfig(manifest: any): T.CommonsMockType {
    return {
      ...manifest?.mock,
      enable: Boolean(manifest?.mock?.enable)
    };
  }

  private static extractAdapterConfig(manifest: any): T.AdapterInputType {
    return manifest?.adapter || {};
  }

  private static extractAuthProvidersConfig(
    manifest: any
  ): T.AuthProvidersPropsType {
    return {
      initialAuthenticationProvider:
        manifest?.services?.login?.initialAuthenticationProvider ||
        this.extractLoginConfig(manifest).authenticationProvider,
      authenticationProvider:
        this.extractLoginConfig(manifest).authenticationProvider
    };
  }

  private static extractAccessControlConfig(
    manifest: any
  ): T.AccessControlInputType {
    const config = manifest?.services?.accessControl;
    return {
      enabled: Boolean(config?.enabled),
      actionsPrefix: config?.actionsPrefix,
      actions: config?.actions || []
    };
  }

  private static extractBreadcrumbConfig(manifest: any): T.BreadcrumbInputType {
    return manifest?.services?.breadcrumb;
  }

  private static extractCriterionsConfig(
    manifest: any
  ): T.CriterionInputType<any>[] {
    return manifest?.criterions || [];
  }

  private static extractEntitlementsConfig(
    manifest: any
  ): T.EntitlementsInputType {
    return {
      enabled: Boolean(manifest?.services?.entitlements?.enabled),
      stack: manifest?.portal?.stack // stack as String
    };
  }

  private static extractFallbackConfig(manifest: any) {
    return {
      defaultFallbackKey: manifest?.fallback?.defaultFallbackKey,
      defaultFallback: {
        strategy: 'redirect',
        redirectTo: manifest?.portal?.fallbackRoute || '/',
        key: undefined
      },
      fallbackList: manifest?.fallback?.fallbackList
    };
  }

  private static extractLocalizationConfig(
    manifest: any
  ): T.LocalizationServiceInputType {
    return manifest?.services?.localization;
  }

  private static extractOnboardingConfig(manifest: any): T.OnboardingInputType {
    const config = manifest?.services?.onboarding;
    return {
      userOnboardingPath: config?.userOnboardingPath,
      enable: Boolean(config?.enable),
      stack: manifest?.portal?.stack, // stack as String
      stages: config?.stages,
      ...config
    };
  }

  private static extractOptimizelyConfig(manifest: any): T.OptimizelyInputType {
    const config = manifest?.services?.optimizely;
    return {
      enable: Boolean(config?.enable),
      snippetId: config?.snippetId
    };
  }

  private static extractUserOnboardingConfig(
    manifest: any
  ): T.UserOnboardingInputType {
    const userOnboardingService = manifest?.services?.userOnboarding;
    const onboardingService = manifest?.services?.onboarding;

    return {
      enable: userOnboardingService?.enable,
      path: userOnboardingService?.path,
      forceUserOnboardingCriterionKey:
        userOnboardingService?.forceUserOnboardingCriterionKey,
      userOnboardingPath: onboardingService?.userOnboardingPath // backward compatibility
    };
  }

  private static extractEventConfig(manifest: any): T.EventServiceInputType {
    return {
      removeSelfFilterByContainerInstanceId:
        manifest?.services?.event?.removeSelfFilterByContainerInstanceId
    };
  }

  private static extractABTestingConfig(manifest: any): T.ABTestingInputType {
    const abTestingConfig = manifest?.services?.abTesting || {};
    const optimizelyConfig = manifest?.services?.optimizely || {}; // backward compatibility

    // priority option, ignoring Optimizely deprecated config
    if (abTestingConfig.enabled) {
      return {
        enabled: Boolean(abTestingConfig?.enabled),
        clientName: abTestingConfig?.clientName,
        snippetId: abTestingConfig?.snippetId
      };
    }
    // Checking if contains optimizely configuration
    if (optimizelyConfig.enable) {
      return {
        enabled: Boolean(optimizelyConfig?.enable),
        clientName: 'optimizely',
        snippetId: optimizelyConfig?.snippetId
      };
    }

    // None configuration is being applied
    return {
      enabled: false,
      clientName: ''
    };
  }

  private static extractAstConfig(manifest: any): T.AstInputType {
    return {
      enabled: Boolean(manifest?.services?.ast?.enable)
    };
  }

  private static extractBackgroundTasksConfig(
    manifest: any
  ): T.BackgroundTasksInputType {
    const config = manifest?.services?.backgroundTasks;
    return {
      enabled: Boolean(config?.enable),
      backgroundTaskRegistrarList: config?.backgroundTaskRegistrarList
    };
  }

  private static extractConsentConfig(
    manifest: any,
    isNative: boolean
  ): T.ConsentServiceInputType {
    const emailGathering = manifest?.services?.login?.emailGathering || false;

    return {
      isEmailGathering: emailGathering,
      isNative: isNative,
      globalInjectConsentBanner:
        manifest?.services?.consentService?.globalInjectConsentBanner ?? true
    };
  }

  private static extractFeatureFlagsConfig(
    manifest: any
  ): T.FeatureFlagsInputType {
    const config = manifest?.services?.featureFlags;
    return {
      enabled: Boolean(config?.enable),
      projectSettingsList: config?.clients || []
    };
  }

  private static extractGrantsConfig(manifest: any): T.GrantsInputType {
    return {
      enabled: manifest?.services?.grants?.enabled
    };
  }

  private static extractGrantsHistoryConfig(
    manifest: any
  ): T.GrantHistoryInputType {
    return {
      enabled: manifest?.services?.grantsHistory?.enabled
    };
  }

  private static extractGraphQLConfig(
    manifest: any
  ): T.GraphQLServiceInputType {
    const config = manifest?.services?.graphql;
    return {
      mock: Boolean(config?.mock),
      platform: config?.platform,
      isShellPortal: Boolean(config?.isShellPortal),
      serverUrl: config?.serverUrl,
      stack: manifest?.portal?.stack, // stack as String
      XApiKey: config?.XApiKey
    };
  }

  private static extractIdleLogoutConfig(manifest: any): T.IdleLogoutInputType {
    return manifest?.services?.idleLogout;
  }

  private static extractLayoutsConfig(manifest: any): T.LayoutInputType[] {
    return manifest?.layouts || [];
  }

  private static extractMonitoringConfig(manifest: any): T.MonitoringInputType {
    const monitoringConfig = manifest?.services?.monitoring;
    const enabled =
      Boolean(monitoringConfig?.enabled) ||
      Boolean(manifest?.services?.splunk?.enabled);

    return {
      enabled,
      splunk: this.extractSplunkConfig(manifest),
      datadog: this.extractDatadogConfig(manifest)
    };
  }

  private static extractSplunkConfig(manifest: any): T.SplunkRumInputType {
    const monitoringConfig = manifest?.services?.monitoring;
    const newSplunkConfig =
      Boolean(monitoringConfig?.enabled) &&
      Boolean(monitoringConfig?.providers?.splunk?.enable);
    return {
      enable: Boolean(manifest?.services?.splunk?.enabled) || newSplunkConfig
    };
  }

  private static extractDatadogConfig(manifest: any): T.DatadogInputType {
    const monitoringConfig = manifest?.services?.monitoring;
    return {
      enable:
        Boolean(monitoringConfig?.enabled) &&
        Boolean(monitoringConfig?.providers?.datadog?.enable)
    };
  }

  private static extractRoutesConfig(
    manifest: any
  ): T.RoutesServiceDependenciesInputType {
    return {
      defaultLayoutKey: manifest?.navigation?.defaultLayoutKey,
      routes: manifest?.navigation?.routes,
      defaultCriterionKey: manifest?.navigation?.defaultCriterionKey,
      defaultNavigationElements: manifest?.navigation?.defaultNavigationElements
    };
  }

  private static extractServiceWorkerConfig(
    manifest: any
  ): T.ServiceWorkerInputType {
    return manifest?.services?.serviceWorker;
  }

  private static extractThemeConfig(manifest: any): T.ThemeInputType {
    return manifest?.theme;
  }

  private static extractTenantHandlerConfig(
    manifest: any,
    isNative: boolean
  ): T.TenantHandlerInputType {
    const manifestTenantHandler = manifest?.services?.tenantHandler;
    const forceTenantSelectionConfig =
      manifestTenantHandler?.forceTenantSelectionOnPublicRoutes;

    return {
      enabled: !!manifestTenantHandler?.enable,
      tenantHandlerList: manifestTenantHandler?.tenantHandlerList,
      globalTenantHandler: manifestTenantHandler?.globalTenantHandler,
      tenantSelectorAssetReference: manifestTenantHandler?.globalTenantHandler,
      stack: this.extractStackConfig(manifest), // stack as ENUM
      isNative: isNative,
      tenantGroups: manifestTenantHandler?.tenantGroups,
      forceTenantSelectionOnPublicRoutes: forceTenantSelectionConfig
        ? {
            enabled: forceTenantSelectionConfig.enabled ?? false,
            fallbackStrategy: {
              strategy:
                forceTenantSelectionConfig.fallbackStrategy?.strategy ||
                'redirect',
              redirectUrl:
                forceTenantSelectionConfig.fallbackStrategy?.redirectUrl || '/',
              modalKey: forceTenantSelectionConfig.fallbackStrategy?.modalKey,
              routesToBlockRedirect:
                forceTenantSelectionConfig.fallbackStrategy
                  ?.routesToBlockRedirect
            }
          }
        : {
            enabled: false,
            fallbackStrategy: {
              strategy: 'redirect',
              redirectUrl: '/'
            }
          }
    };
  }

  private static extractURLServiceConfig(manifest: any): T.URLServiceInputType {
    const localization = manifest?.services?.localization;
    return {
      languageMap: localization?.languages,
      manifestBasePath: manifest?.portal?.basePath,
      removeLocaleUrl: localization?.removeLocaleUrl
    };
  }

  private static extractUserActivityConfig(
    manifest: any
  ): T.UserActivityInputType {
    return manifest?.services?.userActivity;
  }

  private static extractEnvironmentVariablesConfig(
    manifest: any
  ): EnvironmentVariablesInputType {
    // Extract from manifest.environmentVariables (preferred structure)
    const envVars = manifest?.environmentVariables;
    if (envVars && typeof envVars === 'object') {
      return envVars;
    }

    // Return empty object if no environment variables found
    return {};
  }

  private static extractOverridesConfig(manifest: any): T.OverridesInputType {
    const overridesConfig = manifest?.overrides || {};

    return {
      enable: Boolean(overridesConfig.enable)
    };
  }
}
